import { useSupabaseLoader } from "../../../server/supabase/hooks";

import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { ProgramNode } from "../../program-tree/program-tree";
import type { TimelineRow } from "../program-timeline";

/**
 * The Communications of the programs, with their timing, and the number of archived ones. A program
 * can have many archived ones, so their rows load only with `withArchived`. The program policies
 * leave out the ones in a program the caller cannot see.
 */
export const useProgramTimeline = (
  programIds: ProgramNode["id"][],
  withArchived: boolean,
): UseSupabaseLoaderState<{ rows: TimelineRow[]; archived: number }> & { reload: () => void } =>
  useSupabaseLoader(
    async ({ supabase }) => {
      const timeline = () =>
        supabase.from("moment_timeline_with_archived").select("*").in("program_id", programIds);
      const [rows, archived] = await Promise.all([
        withArchived ? timeline() : timeline().neq("status", "archived"),
        supabase
          .from("moment_timeline_with_archived")
          .select("id", { count: "exact", head: true })
          .in("program_id", programIds)
          .eq("status", "archived"),
      ]);
      if (rows.error) throw rows.error;
      if (archived.error) throw archived.error;
      return { rows: rows.data, archived: archived.count ?? 0 };
    },
    [programIds, withArchived],
  );

/**
 * The Communications of the other programs that send from a person's date or on a specific date,
 * for the timings of the timeline and the list. Archived ones do not count. The program policies
 * leave out the ones in a program the caller cannot see.
 */
export const useElsewhere = (
  programIds: ProgramNode["id"][],
): UseSupabaseLoaderState<TimelineRow[]> & { reload: () => void } =>
  useSupabaseLoader(
    async ({ supabase }) => {
      const { data, error } = await supabase
        .from("moment_timeline_with_archived")
        .select("*")
        .not("program_id", "in", `(${programIds.join(",")})`)
        .neq("status", "archived")
        .in("kind", ["subject", "fixed"]);
      if (error) throw error;
      return data;
    },
    [programIds],
  );
